import type { ComponentProps, FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { ADDON_TYPE_NAME_DICT } from "app/contexts/AddonStateContext/const";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { translate } from "app/filters/i18nFilter";
import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import type { IColumnsConfigArguments } from "app/pages/keyword-research/website-analysis/pages/website-keywords/table/WebsiteKeywordsPage2TableColumns";
import { getColumnsConfig } from "app/pages/keyword-research/website-analysis/pages/website-keywords/table/WebsiteKeywordsPage2TableColumns";
import { TablePageFilterWithQueryParamsEmptyState } from "app/pages/website-analysis/components/TablePageFilterWithQueryParamsEmptyState";
import { getPaidExcelProps } from "app/services/PaidExcelService/getPaidExcelProps";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { hasQueryParameters } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { CreateOrUpdateKeywordListPopoverWrapper } from "pro-features/components/TableSelection/src/single-responsibility-buttons/keyword-list/CreateOrUpdateKeywordListPopoverWrapper";
import { SearchTrackerFromKeywordsTablePopover } from "pro-features/components/TableSelection/src/single-responsibility-buttons/search-tracker/SearchTrackerPopover";

import { WebsiteKeywordsEnriched } from "../enrichedTable/WebsiteKeywordsEnriched";
import { useWebsiteKeywordsPage2TableDashboard } from "../hooks/useWebsiteKeywordsPage2TableDashboard";
import { useWebsiteKeywordsPage2TableSelectionButtons } from "../hooks/useWebsiteKeywordsPage2TableSelectionButtons";
import { useWebsiteKeywordsPage2Context } from "../WebsiteKeywordsPage2Context";
import type { KeywordTransformedData } from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";

import { WebsiteKeywordsPage2Graph } from "./clicks-over-time-graph/WebsiteKeywordsPage2Graph";
import { PAGE_SIZE } from "app/common/keyword-research/website-keywords/tableConsts";
import { WebsiteKeywordsPage2TableWrapper } from "./WebsiteKeywordsPage2TableStyledComponents";

interface IWebsiteKeywordsPage2TableProps {
    withAddToDashboard?: boolean;
    getColumnsFunction?: (args: IColumnsConfigArguments) => any;
    keywordGroupState?: string;
}

export const WebsiteKeywordsPage2Table: FC<IWebsiteKeywordsPage2TableProps> = ({
    withAddToDashboard = true,
    getColumnsFunction = getColumnsConfig,
    keywordGroupState = "organicsearch_keywordAnalysis_2",
}) => {
    const { country, duration, key, selectedPageTab, pageFilter, comparedDuration, webSource } =
        useSearchAdvertisingDataAndParams();

    const [selectedRows, setSelectedRows] = useState<KeywordTransformedData[]>([]);
    const [expandedRowKey, setExpandedRowKey] = useState<string[]>([]);

    const { data, isError, isLoading, currentPage, setCurrentPage, sort, setSort, excelUrl, requestBody } =
        useWebsiteKeywordsPage2Context();

    const { openModal: openAddToDashboardModal, modalElement: addToDashboardModalElement } =
        useWebsiteKeywordsPage2TableDashboard();

    const pageArr: IChosenItemParams = safeJsonParse(pageFilter, []) as IChosenItemParams;
    const isPageWithQueryParams = hasQueryParameters(pageArr[0].url);

    const toggleExpandRow = useCallback((rowKey: string) => {
        setExpandedRowKey((prevExpandedKeys) => (prevExpandedKeys.includes(rowKey) ? [] : [rowKey]));
    }, []);

    const onPageChangeHandler = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const closeExpandedRow = () => {
        setExpandedRowKey([]);
    };

    const onSort: ComponentProps<typeof Table<KeywordTransformedData>>["onSort"] = (sorter) => {
        const isAsc = sorter.order === "ascend";
        setSort({
            sort: sorter.columnKey as string,
            asc: isAsc,
        });
        setCurrentPage(1);
    };

    const onSelectionChange = useCallback((_, rows: KeywordTransformedData[]) => {
        setSelectedRows(rows);
    }, []);

    const { hasAddonsHub } = useAddonsState();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const tableColumns = useMemo(
        () =>
            getColumnsFunction({
                country,
                duration,
                webSource,
                currentPage,
                selectedPageTab,
                comparedDuration,
                toggleExpandRow,
                sortConfig: sort,
                pageSize: PAGE_SIZE,
                sourceType: selectedPageTab,
                totalCount: data.TotalCount,
                domains: pageFilter ? safeJsonParse<string[]>(pageFilter, [] as string[]) : key.split(","),
            } as IColumnsConfigArguments) as ColumnsType<KeywordTransformedData>,
        [
            data,
            country,
            pageFilter,
            key,
            selectedPageTab,
            webSource,
            sort,
            duration,
            comparedDuration,
            currentPage,
            toggleExpandRow,
            getColumnsFunction,
        ],
    );

    const getErrorObject = () => {
        return pageArr?.length === 1 && isPageWithQueryParams
            ? {
                  Component: TablePageFilterWithQueryParamsEmptyState,
                  showHeaderWhenEmpty: true,
              }
            : {
                  title: translate("keyword.research.search.ads.table.error.title"),
                  subtitle: translate("keyword.research.search.ads.table.error.subtitle"),
              };
    };

    const noDataObject = isError
        ? getErrorObject()
        : {
              title: translate("keyword.research.search.ads.table.no.data.title"),
              subtitle: translate("keyword.research.search.ads.table.no.data.subtitle"),
          };

    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "click", "Table/Excel");
    };

    const hasPaidExcel = !!isPaidExcelAllowed();

    const paidExcelProps = useMemo(() => getPaidExcelProps(), []);

    const {
        actionButtons,
        searchTrackerButtonRef,
        isSearchTrackerPopoverOpen,
        isGraphDisplayed,
        setIsGraphDisplayed,
        setIsSearchTrackerPopoverOpen,
        ...keywordListSelectionProps
    } = useWebsiteKeywordsPage2TableSelectionButtons(setSelectedRows, isLoading, data.Data, selectedRows.length);

    const selectedRowsKeywords = useMemo(() => {
        return selectedRows.map((row) => row.keyword);
    }, [selectedRows]);

    useEffect(() => {
        if (isGraphDisplayed && selectedRowsKeywords.length === 0) {
            setIsGraphDisplayed(false);
        }
    }, [isGraphDisplayed, selectedRowsKeywords.length, setIsGraphDisplayed]);

    const onCloseButtonClick = () => {
        setIsGraphDisplayed(false);
    };

    const isSinglePeriodView = !comparedDuration;

    return (
        <WebsiteKeywordsPage2TableWrapper>
            {addToDashboardModalElement}
            <Table<KeywordTransformedData>
                columnConfig={tableColumns}
                data={data.Data}
                localStorageKey="WebsiteKeywords2.0_Table"
                isLoading={isLoading}
                loaderRowCount={PAGE_SIZE / 2}
                noData={noDataObject}
                size="large"
                sticky={false}
                selection={{
                    enabled: true,
                    onChange: onSelectionChange,
                    preserveSelectedRowKeys: true,
                    selectedRowKeys: selectedRowsKeywords,
                }}
                pagination={{
                    pageSize: PAGE_SIZE,
                    total: data.TotalCount,
                    current: currentPage,
                    onChange: (_, __, newPage) => onPageChangeHandler(newPage),
                }}
                expandable={{
                    // eslint-disable-next-line react/no-unstable-nested-components
                    expandedRowRender: (record: KeywordTransformedData) => (
                        <WebsiteKeywordsEnriched
                            row={record}
                            closeExpandedRow={closeExpandedRow}
                        />
                    ),
                    expandIcon: () => null, // Hide default expand icon
                    expandedRowKeys: expandedRowKey,
                    onExpand: (_, record: { key: string }) => {
                        toggleExpandRow(record.key);
                    },
                    expandIconColumnIndex: -1,
                    expandedRowClassName: (record: { key: string }) =>
                        expandedRowKey.length > 0 && expandedRowKey[0] === record.key ? "expanded-row-active" : "",
                }}
                onSort={onSort}
            >
                <Table.TableTop>
                    <Table.TableTop.Selection
                        selectText="Keywords selected"
                        actionButtons={actionButtons}
                        selectionTooltip={translate("website.keywords.table.selection.tooltip")}
                    />
                    <Table.TableTop.Settings
                        locale={{
                            columnPicker: {
                                mandatoryField: translate("keyword.analysis.table.column.picker.mandatory"),
                            },
                        }}
                        columnPickerType="reorderable"
                        showAddToDashboard={withAddToDashboard ? isSinglePeriodView : false}
                        onAddToDashboard={openAddToDashboardModal}
                        excelExportConfig={{
                            excelDownloadUrl: swSettings.current.resources.IsExcelAllowed ? excelUrl : null,
                            hasAddonHub: hasAddonsHub,
                            onExcelDownloaded: trackExcelDownload,
                            onCtaClicked: () => handlePurchaseAddonClick(ADDON_TYPE_NAME_DICT.DataCreditsDynamic),
                            totalCount: data.TotalCount,
                            excelBodyForReq: requestBody,
                            ...paidExcelProps,
                            withPaidExcel: hasPaidExcel,
                        }}
                    />
                    {isGraphDisplayed ? (
                        <Table.TableTop.Chart>
                            <WebsiteKeywordsPage2Graph
                                selectedRowsKeywords={selectedRowsKeywords}
                                onCloseButtonClick={onCloseButtonClick}
                            />
                        </Table.TableTop.Chart>
                    ) : null}
                </Table.TableTop>
            </Table>
            <CreateOrUpdateKeywordListPopoverWrapper
                {...keywordListSelectionProps}
                selectedRows={selectedRowsKeywords}
                keywordGroupState={keywordGroupState}
            />
            <SearchTrackerFromKeywordsTablePopover
                selectedRows={selectedRowsKeywords}
                searchTrackerButtonRef={searchTrackerButtonRef}
                isSearchTrackerPopoverOpen={isSearchTrackerPopoverOpen}
                setIsSearchTrackerPopoverOpen={setIsSearchTrackerPopoverOpen}
            />
        </WebsiteKeywordsPage2TableWrapper>
    );
};
